import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Clock, Users, Award, BookOpen, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const TrainingDetailPage = () => {
  const { programId } = useParams();
  const { user, token } = useAuth();
  const [program, setProgram] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/training-programs/${programId}`),
      token ? axios.get(`${API}/my-enrollments`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: { enrollments: [] } })) : Promise.resolve({ data: { enrollments: [] } }),
    ]).then(([progRes, enrollRes]) => {
      setProgram(progRes.data);
      const myEnrollments = enrollRes.data.enrollments || [];
      setEnrolled(myEnrollments.some(e => e.program_id === programId));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [programId, token]);

  const handleEnroll = async () => {
    if (!token) { toast.error('Please login to enroll'); return; }
    setEnrolling(true);
    try {
      await axios.post(`${API}/training-programs/${programId}/enroll`, {}, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      toast.success('Enrolled successfully!');
      setEnrolled(true);
      setProgram(p => ({ ...p, current_enrollments: (p.current_enrollments || 0) + 1 }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enroll');
    }
    setEnrolling(false);
  };

  const handleUnenroll = async () => {
    setEnrolling(true);
    try {
      await axios.delete(`${API}/training-programs/${programId}/enroll`, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
      });
      toast.success('Unenrolled');
      setEnrolled(false);
      setProgram(p => ({ ...p, current_enrollments: Math.max(0, (p.current_enrollments || 0) - 1) }));
    } catch { toast.error('Failed to unenroll'); }
    setEnrolling(false);
  };

  if (loading) return (
    <PublicLayout><div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div></PublicLayout>
  );

  if (!program) return (
    <PublicLayout>
      <div className="text-center py-20">
        <h2 className="text-xl font-bold text-slate-700">Program not found</h2>
        <Link to="/training"><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button></Link>
      </div>
    </PublicLayout>
  );

  const levelColors = { beginner: 'bg-green-100 text-green-700', intermediate: 'bg-blue-100 text-blue-700', advanced: 'bg-purple-100 text-purple-700' };

  return (
    <PublicLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="training-detail">
        <Link to="/training" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Programs
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Badge className={levelColors[program.level] || levelColors.beginner}>{program.level}</Badge>
                <Badge variant="outline">{program.category}</Badge>
                {program.is_free && <Badge className="bg-green-100 text-green-700">Free</Badge>}
                {program.certificate_offered && <Badge variant="outline"><Award className="w-3 h-3 mr-1 inline" />Certificate</Badge>}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }} data-testid="program-title">
                {program.title}
              </h1>
              <p className="text-slate-500">By {program.provider_name || 'EdAssist'}</p>
            </div>

            <div className="prose prose-slate max-w-none">
              <p className="text-slate-700 leading-relaxed" data-testid="program-description">{program.description}</p>
            </div>

            {program.syllabus?.length > 0 && (
              <Card>
                <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Syllabus</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {program.syllabus.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                        <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-indigo-600">{i + 1}</span>
                        </div>
                        <span className="text-sm text-slate-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {program.prerequisites && (
              <Card>
                <CardHeader><CardTitle style={{ fontFamily: 'Outfit' }}>Prerequisites</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600">{program.prerequisites}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card className="sticky top-6">
              <CardContent className="p-6 space-y-4">
                <div className="text-center">
                  {program.is_free ? (
                    <p className="text-3xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }}>Free</p>
                  ) : (
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                      {program.currency === 'INR' ? '₹' : '$'}{program.price?.toLocaleString()}
                    </p>
                  )}
                </div>

                {enrolled ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600 justify-center">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Enrolled</span>
                    </div>
                    <Button variant="outline" className="w-full" onClick={handleUnenroll} disabled={enrolling} data-testid="unenroll-btn">
                      Unenroll
                    </Button>
                  </div>
                ) : (
                  <Button className="w-full" size="lg" onClick={handleEnroll} disabled={enrolling} data-testid="enroll-btn">
                    {enrolling ? 'Enrolling...' : 'Enroll Now'}
                  </Button>
                )}

                {program.external_link && (
                  <a href={program.external_link} target="_blank" rel="noreferrer" className="block">
                    <Button variant="outline" className="w-full"><ExternalLink className="w-4 h-4 mr-2" />Visit Partner Site</Button>
                  </a>
                )}

                <div className="space-y-3 pt-4 border-t text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{program.duration || 'Self-paced'}</div>
                  <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-slate-400" />{program.format || 'Online'}</div>
                  <div className="flex items-center gap-2"><Users className="w-4 h-4 text-slate-400" />{program.current_enrollments || 0} enrolled</div>
                  {program.certificate_offered && <div className="flex items-center gap-2"><Award className="w-4 h-4 text-slate-400" />Certificate on completion</div>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default TrainingDetailPage;
